
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle } from 'lucide-react';
import { LevelConfig } from '@/models/LearnTypes';
import { usePerfectLevel } from '@/contexts/PerfectLevelContext';
import PerfectLevelIndicator from './PerfectLevelIndicator';
import LevelXPDisplay from './LevelXPDisplay';
import LevelProgressBar from './LevelProgressBar';

interface LevelCompleteProps {
  level: LevelConfig;
  currentLevelIndex: number;
  totalLevels: number;
  onNextLevel: () => void;
  isPerfectLevel?: boolean;
  adjustedXpReward?: number;
  isPracticeMode?: boolean;
}

const LevelComplete = ({
  level,
  currentLevelIndex,
  totalLevels,
  onNextLevel,
  isPerfectLevel = false,
  adjustedXpReward,
  isPracticeMode = false
}: LevelCompleteProps) => {
  const isSpellingLevel = level.type === 'spelling';
  
  // Use our simplified context-based perfect status tracking
  const { getLevelPerfectStatus } = usePerfectLevel();
  const isPerfect = getLevelPerfectStatus(currentLevelIndex);
  
  // Use the adjusted XP if provided, otherwise fall back to the level's XP reward
  const xpEarned = adjustedXpReward !== undefined ? adjustedXpReward : level.xpReward;
  
  // Fixed 5 XP bonus for perfect level completion
  const perfectBonusXp = 5;
  
  // Log final status for debugging
  console.log("🏆 [LevelComplete] Perfect status for level", currentLevelIndex, ":", isPerfect, "with bonus:", isPerfect ? perfectBonusXp : 0);
  
  return (
    <div className="animate-fade-in space-y-6"
      data-level-id={currentLevelIndex}
      data-level-perfect={isPerfect.toString()}
      data-level-type={level.type}
    >
      <Card className="border-none shadow-lg animate-scale-in">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Level Complete!</CardTitle>
          <CardDescription className="text-lg">
            You've completed {level.title}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-8 pt-4">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center animate-[scale-in_0.5s_ease-out]">
            <CheckCircle className="h-10 w-10 text-primary" />
          </div>
          
          <div className="text-center">
            <LevelXPDisplay 
              xpEarned={xpEarned} 
              showRestartButton={false}
              isPracticeMode={isPracticeMode} 
            />
            
            <PerfectLevelIndicator 
              isPerfect={isPerfect} 
              isSpellingLevel={isSpellingLevel} 
              bonusAmount={perfectBonusXp}
              isPracticeMode={isPracticeMode}
            />
            
            <LevelProgressBar 
              currentLevelIndex={currentLevelIndex} 
              totalLevels={totalLevels} 
            />
          </div>
          
          <div className="w-full">
            <Button 
              className="w-full" 
              size="lg"
              onClick={onNextLevel}
            >
              {currentLevelIndex < totalLevels - 1 ? 'Next Level' : 'Complete Lesson'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LevelComplete;
